import WorkflowModel from '@opsbase/lib-data-model/models/WorkflowModel';
import QueryFieldKey from '@opsbase/lib-data-queryfield/QueryFieldKey';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import { Condition } from '@opsbase/lib-workflow/WorkflowCondition';
import OperatorType from '@opsbase/lib-workflow/WorkflowCondition/OperatorType';
import useSelf from '@opsbase/react-ctrl-self/useSelf/useSelf';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import AddIcon from '@opsbase/web-icons/icons/AddIcon';
import DeleteIcon from '@opsbase/web-icons/icons/DeleteIcon';
import Divider from '@opsbase/web-layout/Divider/Divider';
import Horizontal from '@opsbase/web-layout/Horizontal/Horizontal';
import Vertical from '@opsbase/web-layout/Vertical/Vertical';
import Button from '@opsbase/web-ui/Button/Button';
import IconButton from '@opsbase/web-ui/IconButton';
import Text from '@opsbase/web-ui/Text/Text';
import WorkflowConditionPicker from '@opsbase/web-workflow/WorkflowConditionPicker';
import flatten from 'lodash/flatten';
import React, { useEffect, useMemo, useState } from 'react';
import WorkflowDialogColumn from './WorkflowDialogColumn';
export function WorkflowDialogConditions(props) {
    var _a, _b, _c, _d, _e, _f;
    const self = useSelf();
    const { workflow = new WorkflowModel(), onChange, editable } = props;
    const [expConditionIdx, setExpConditionIdx] = useState({ i: 0, j: 0 });
    const opDefinitionId = (_e = (_d = (_c = (_b = flatten((_a = workflow === null || workflow === void 0 ? void 0 : workflow.orConditions) === null || _a === void 0 ? void 0 : _a.map(o => { var _a; return (_a = o.andConditions) === null || _a === void 0 ? void 0 : _a.filter(a => a.queryFieldKey === QueryFieldKey.OPDEFINITION); }))) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.id) === null || _e === void 0 ? void 0 : _e[0];
    const setCondition = useMemo(() => (c, i, j, setExpanded = true) => {
        var _a, _b, _c, _d;
        if ((_a = workflow === null || workflow === void 0 ? void 0 : workflow.orConditions) === null || _a === void 0 ? void 0 : _a[i]) {
            if ((_c = (_b = workflow.orConditions[i]) === null || _b === void 0 ? void 0 : _b.andConditions) === null || _c === void 0 ? void 0 : _c[j]) {
                workflow.orConditions[i].andConditions[j] = c;
            }
            else {
                workflow.orConditions[i].andConditions.push(c);
            }
        }
        else {
            (_d = workflow === null || workflow === void 0 ? void 0 : workflow.orConditions) === null || _d === void 0 ? void 0 : _d.push({ andConditions: [c] });
        }
        onChange(new WorkflowModel(workflow));
        if (setExpanded) {
            setExpConditionIdx({ i, j });
        }
    }, [workflow, onChange]);
    const handleChangeQueryField = useCallforward((i, j) => (p) => {
        let condition = new Condition({
            queryFieldKey: p,
            value: undefined,
            operator: OperatorType.EQUAL,
        });
        setCondition(condition, i, j);
        if (p === QueryFieldKey.OP_QUESTION) {
            condition = new Condition({
                queryFieldKey: QueryFieldKey.OP_ANSWER,
                value: undefined,
                operator: OperatorType.EQUAL,
            });
            setCondition(condition, i, j + 1, false);
        }
    }, [setCondition]);
    const handleChangeOperator = useCallforward((i, j) => (o) => {
        var _a, _b, _c;
        let condition = new Condition({
            queryFieldKey: ((_c = (_b = (_a = workflow.orConditions[i]) === null || _a === void 0 ? void 0 : _a.andConditions) === null || _b === void 0 ? void 0 : _b[j]) === null || _c === void 0 ? void 0 : _c.queryFieldKey) || '',
            value: undefined,
            operator: o,
        });
        setCondition(condition, i, j);
    }, [setCondition, workflow.orConditions]);
    const handleChangeValue = useCallforward((i, j) => (value) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const queryFieldKey = (_c = (_b = (_a = workflow.orConditions[i]) === null || _a === void 0 ? void 0 : _a.andConditions) === null || _b === void 0 ? void 0 : _b[j]) === null || _c === void 0 ? void 0 : _c.queryFieldKey;
        const isQuestionConditionValue = queryFieldKey === QueryFieldKey.OP_QUESTION;
        const isAnswerConditionValue = queryFieldKey === QueryFieldKey.OP_ANSWER;
        let condition = new Condition({
            queryFieldKey: ((_f = (_e = (_d = workflow.orConditions[i]) === null || _d === void 0 ? void 0 : _d.andConditions) === null || _e === void 0 ? void 0 : _e[j]) === null || _f === void 0 ? void 0 : _f.queryFieldKey) || '',
            value: value,
            operator: (_j = (_h = (_g = workflow.orConditions[i]) === null || _g === void 0 ? void 0 : _g.andConditions) === null || _h === void 0 ? void 0 : _h[j]) === null || _j === void 0 ? void 0 : _j.operator,
        });
        setCondition(condition, i, j, !isAnswerConditionValue);
        //if question condition, reset the corresponding answer condition
        if (isQuestionConditionValue) {
            condition = new Condition({
                queryFieldKey: QueryFieldKey.OP_ANSWER,
                value: undefined,
                operator: OperatorType.EQUAL,
            });
            setCondition(condition, i, j + 1, false);
        }
    }, [setCondition, workflow.orConditions]);
    const handleAddAndCondition = useCallforward((i) => () => {
        const condition = new Condition({ queryFieldKey: '', operator: '', value: [] });
        workflow.orConditions[i].andConditions.push(condition);
        onChange(new WorkflowModel(workflow));
        setExpConditionIdx({ i, j: workflow.orConditions[i].andConditions.length - 1 });
    }, [workflow, onChange]);
    const handleAddOrCondition = useCallforward((i) => () => {
        var _a;
        let mandatoryConditions = workflow.orConditions[0].andConditions.filter(c => !!c.mandatory);
        if (((_a = workflow === null || workflow === void 0 ? void 0 : workflow.orConditions) === null || _a === void 0 ? void 0 : _a.length) > i) {
            workflow.orConditions.splice(i + 1, 0, {
                andConditions: [
                    ...mandatoryConditions,
                    new Condition({ queryFieldKey: '', operator: '', value: [] }),
                ],
            });
        }
        else {
            workflow.orConditions.push({
                andConditions: [
                    ...mandatoryConditions,
                    new Condition({ queryFieldKey: '', operator: '', value: [] }),
                ],
            });
        }
        onChange(new WorkflowModel(workflow));
        setExpConditionIdx({ i: i + 1, j: mandatoryConditions.length });
    }, [workflow, onChange]);
    const handleConditionDelete = useCallforward((i, j) => () => {
        if (workflow.orConditions.length === 1 &&
            workflow.orConditions[i].andConditions.length === 1) {
            return;
        }
        if (workflow.orConditions[i].andConditions[j].queryFieldKey ===
            QueryFieldKey.OP_QUESTION) {
            workflow.orConditions[i].andConditions.splice(j, 1);
        }
        workflow.orConditions[i].andConditions.splice(j, 1);
        if (workflow.orConditions[i].andConditions.filter(c => !c.mandatory).length === 0 &&
            i !== 0) {
            workflow.orConditions.splice(i, 1);
        }
        onChange(new WorkflowModel(workflow));
        setExpConditionIdx(undefined);
    }, [workflow, onChange]);
    const handleExpandCondition = useCallforward((i, j) => () => {
        if ((expConditionIdx === null || expConditionIdx === void 0 ? void 0 : expConditionIdx.i) === i && (expConditionIdx === null || expConditionIdx === void 0 ? void 0 : expConditionIdx.j) === j) {
            setExpConditionIdx(undefined);
        }
        else {
            setExpConditionIdx({ i, j });
        }
    }, [expConditionIdx]);
    useEffect(() => {
        var _a;
        if (!((_a = workflow === null || workflow === void 0 ? void 0 : workflow.orConditions) === null || _a === void 0 ? void 0 : _a.length)) {
            workflow.orConditions.push({
                andConditions: [
                    {
                        queryFieldKey: undefined,
                        value: undefined,
                        operator: undefined,
                    },
                ],
            });
            setExpConditionIdx({ i: expConditionIdx.i, j: expConditionIdx.j });
        }
    }, [workflow, expConditionIdx]);
    const renderCondition = useMemo(() => (c, nextCondition, i, j) => {
        if (c.queryFieldKey === QueryFieldKey.OP_ANSWER)
            return null;
        return (React.createElement(Horizontal, { gutter: true, centered: true, key: 'cond' + i + j },
            React.createElement(WorkflowConditionPicker, { disabled: c.mandatory || !editable, expanded: !c.mandatory &&
                    expConditionIdx &&
                    expConditionIdx.i === i &&
                    expConditionIdx.j === j, onExpand: handleExpandCondition(i, j), event: workflow === null || workflow === void 0 ? void 0 : workflow.event, c: c, answerCondition: c.queryFieldKey === QueryFieldKey.OP_QUESTION ? nextCondition : null, opDefinitionId: opDefinitionId, onChangeQueryFieldSelection: handleChangeQueryField(i, j), onChangeOperatorSelection: handleChangeOperator(i, j), onChangeValue: handleChangeValue(i, j), onChangeAnswerValue: handleChangeValue(i, j + 1), onChangeAnswerOperatorSelection: handleChangeOperator(i, j + 1) }),
            !c.mandatory && (React.createElement(IconButton, { disabled: !editable, onClick: handleConditionDelete(i, j), icon: React.createElement(DeleteIcon, { size: 20, color: Palette.PastelBlue }) }))));
    }, [
        expConditionIdx,
        handleChangeOperator,
        handleChangeQueryField,
        handleChangeValue,
        handleConditionDelete,
        handleExpandCondition,
        editable,
        opDefinitionId,
        workflow,
    ]);
    return (React.createElement(WorkflowDialogColumn, { title: i18n.t('if') }, (_f = workflow === null || workflow === void 0 ? void 0 : workflow.orConditions) === null || _f === void 0 ? void 0 : _f.map((oc, i) => (React.createElement(Vertical, { key: 'orC' + i, shrink: true },
        i > 0 ? (React.createElement(Divider, { style: {
                paddingTop: 5,
                paddingBottom: 5,
                borderTop: `1px solid ${Palette.PastelBlue.main}`,
            } })) : undefined,
        React.createElement(Text, { variant: 'h2' }, i === 0 ? '' : i18n.t('or')),
        React.createElement(Vertical, { gutter: true }, oc.andConditions.map((c, j) => renderCondition(c, oc.andConditions[j + 1], i, j))),
        React.createElement(Horizontal, { gutter: true, shrink: true, justify: 'flex-start', paddingY: true },
            React.createElement(Button, { background: true, disabled: !editable, color: Palette.secondary, label: i18n.t('and').toUpperCase(), icon: React.createElement(AddIcon, null), onClick: handleAddAndCondition(i) }),
            React.createElement(Button, { background: true, disabled: !editable, color: Palette.secondary, label: i18n.t('or'), icon: React.createElement(AddIcon, null), onClick: handleAddOrCondition(i) })))))));
}
export default WorkflowDialogConditions;
